import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import RequireAuth from '@rbac/RequireAuth';
import { RequirePermission } from '@rbac/Can';
import { getAuth, setAuth, clearAuth, PERMISSION_FORMAT } from '@rbac/permissions';
import { useAuth } from '@rbac/useCan';
import { RbacProvider } from '@dreamtree-org/tenancy/react';
import { useAppApi } from '@/lib/api';
import DefaultLayout from '@layouts/DefaultLayout';
import { buildRoutes } from '@/routes';
import { useModules } from '@/modules';
import Login from '@pages/Login';
import CompanyLogin from '@pages/auth/CompanyLogin';
import AcceptInvite from '@pages/auth/AcceptInvite';
import ResetPassword from '@pages/auth/ResetPassword';
import AccountSecurity from '@pages/auth/AccountSecurity';
import { isMultiTenant, refreshMe } from '@/lib/tenantAuth';
import Unauthorized from '@pages/Unauthorized';
import NotFound from '@pages/NotFound';
import EmbedChat from '@pages/EmbedChat';
import AgentDetail from '@pages/AgentDetail';
import DynamicToolDetail from '@pages/DynamicToolDetail';
import KnowledgeBaseDetail from '@pages/KnowledgeBaseDetail';
import ToolProviderDetail from '@pages/ToolProviderDetail';
import McpServersPage from '@pages/McpServersPage';
import McpServerDetail from '@pages/McpServerDetail';
import ConnectionDetail from '@pages/ConnectionDetail';
import VoiceCallDetail from '@pages/VoiceCallDetail';
import WorkflowDetail from '@pages/WorkflowDetail';
import MockApiDetail from '@pages/MockApiDetail';
import UiAuditDetail from '@pages/UiAuditDetail';
import CompanyDetail from '@pages/CompanyDetail';
import ConnectionCategoryDetail from '@pages/ConnectionCategoryDetail';
import ConnectionProviderDetail from '@pages/ConnectionProviderDetail';
import AgentFeedbackDetail from '@pages/AgentFeedbackDetail';
import WebhookEndpointDetail from '@pages/WebhookEndpointDetail';
import ApiTokenDetail from '@pages/ApiTokenDetail';
import PlatformLogin from '@/platform/PlatformLogin';
import PlatformTotpSetup from '@/platform/PlatformTotpSetup';
import PlatformOverview from '@/platform/PlatformOverview';
import PlatformCompanies from '@/platform/PlatformCompanies';
import PlatformCompanyDetail from '@/platform/PlatformCompanyDetail';
import PlatformAudit from '@/platform/PlatformAudit';
import { RequirePlatformSession, PlatformLayout } from '@/platform/PlatformSession';
import ApplicationDetail from '@pages/ApplicationDetail';

function ToolProviderIdRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app-tool-provider/${id}`} replace />;
}

export default function App() {
  const { modules, loaded } = useModules();
  const routes = buildRoutes(modules);
  const { fetchApi } = useAppApi();
  const user = useAuth();
  // Multi-tenant: render nothing until the session check answers, or RequireAuth would bounce
  // a signed-in user to /login on every cold load.
  const [tenantReady, setTenantReady] = useState(!isMultiTenant());

  // Refresh the cached profile+permissions from the server on every app load (not just
  // right after login) — self-heals a stale cache (e.g. one saved before /api/me existed,
  // or after a role/permission change) without requiring an explicit re-login.
  useEffect(() => {
    if (isMultiTenant()) {
      // Company sessions: the cookie is HttpOnly, so ask the server rather than trusting the cache.
      refreshMe().catch(() => clearAuth()).finally(() => setTenantReady(true));
      return;
    }
    if (!getAuth()) return; // no session cookie to even try — RequireAuth sends to /login
    fetchApi('me').then(setAuth).catch(() => clearAuth());
  }, []); // deliberately once-on-mount, not on every fetchApi identity change

  // Deliberately don't render the route table (and its "*" → /404 catch-all) until the
  // *real* module list has loaded at least once. The build-time seed in modules.js has
  // caused real hard-navigation 404s before: a newly added module's route isn't in the
  // seed, the catch-all redirects to /404 on first paint, and by the time the real
  // fetch resolves moments later the app is already sitting at /404 with no automatic
  // way back (a redirect is a one-time event, not something a later re-render undoes).
  // A brief loading flicker is a strictly better failure mode than a wrong 404.
  if (!tenantReady) return null;
  if (!loaded && getAuth()) return null;

  return (
    // Permissions come from the app's own auth store (`app:auth`, colon wire format);
    // <Can>/<RequirePermission>/useCan read them through this provider.
    <RbacProvider user={user} format={PERMISSION_FORMAT} Navigate={Navigate} loginRoute="/login" unauthorizedRoute="/unauthorized">
    <Routes>
      {/* public */}
      <Route path="/login" element={isMultiTenant() ? <CompanyLogin /> : <Login />} />
      {/* Company sign-in (multi-tenant mode): a company's own address and invitation links. */}
      <Route path="/c/:slug/login" element={<CompanyLogin />} />
      <Route path="/c/:slug/accept-invite" element={<AcceptInvite />} />
      <Route path="/c/:slug/reset-password" element={<ResetPassword />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      {/* Public Embeddable Chat Widget (PRD §3.9) — no RequireAuth, no DefaultLayout */}
      <Route path="/embed/agent/:agentKey" element={<EmbedChat />} />

      {/* Platform console — the superadmin realm (routes/platformRouter.js). Its own session
          cookie and guard; never the tenant RequireAuth/DefaultLayout, so neither realm's
          session can open the other's screens. */}
      <Route path="/platform/login" element={<PlatformLogin />} />
      <Route element={<RequirePlatformSession />}>
        <Route path="/platform/setup-2fa" element={<PlatformTotpSetup />} />
        <Route element={<PlatformLayout />}>
          <Route path="/platform" element={<PlatformOverview />} />
          <Route path="/platform/companies" element={<PlatformCompanies />} />
          <Route path="/platform/companies/:id" element={<PlatformCompanyDetail />} />
          <Route path="/platform/audit" element={<PlatformAudit />} />
        </Route>
      </Route>

      {/* protected */}
      <Route element={<RequireAuth />}>
        {/* Standalone: a company admin without two-step can reach nothing else yet. */}
        <Route path="/account/security" element={<AccountSecurity />} />
        <Route element={<DefaultLayout />}>
          {routes.map((r) => (
            <Route
              key={r.path}
              path={r.path}
              element={<RequirePermission module={r.module} action={r.action}>{r.element}</RequirePermission>}
            />
          ))}
          {/* Parameterized routes don't fit buildRoutes()'s one-route-per-model shape */}
          <Route
            path="/app-ai-agent/:id"
            element={<RequirePermission module="agents" action="view"><AgentDetail /></RequirePermission>}
          />
          <Route
            path="/app-dynamic-tool/:id"
            element={<RequirePermission module="tools" action="view"><DynamicToolDetail /></RequirePermission>}
          />
          {/* Canonical Tool Providers detail — unified for all kinds (http/mcp/client).
              Legacy aliases /mcp-server/:id and /tool-providers/:id redirect here so
              bookmarks and old agent-detail links keep working. */}
          <Route
            path="/app-tool-provider/:id"
            element={<RequirePermission module="toolproviders" action="view"><ToolProviderDetail /></RequirePermission>}
          />
          <Route
            path="/mcp-server/:id"
            element={<RequirePermission module="toolproviders" action="view"><McpServerDetail /></RequirePermission>}
          />
          <Route
            path="/tool-providers/:id"
            element={<RequirePermission module="toolproviders" action="view"><ToolProviderIdRedirect /></RequirePermission>}
          />
          {/* Legacy list alias: /mcp-server was a separate MCP-only screen.
              Now Tool Providers has kind tabs; this keeps old bookmarks working. */}
          <Route
            path="/mcp-server"
            element={<RequirePermission module="toolproviders" action="view"><McpServersPage /></RequirePermission>}
          />
          <Route path="/tool-providers" element={<Navigate to="/app-tool-provider" replace />} />
          <Route
            path="/app-knowledge-base/:id"
            element={<RequirePermission module="knowledge" action="view"><KnowledgeBaseDetail /></RequirePermission>}
          />
          <Route
            path="/app-connection/:id"
            element={<RequirePermission module="connections" action="view"><ConnectionDetail /></RequirePermission>}
          />
          <Route
            path="/app-voice-call/:id"
            element={<RequirePermission module="calls" action="view"><VoiceCallDetail /></RequirePermission>}
          />
          {/* Workflows are created by the dialog on the list page, which posts the row
              before opening the editor — so the editor never handles an unsaved workflow.
              This catches links and bookmarks left over from when /new was the create
              surface, and must stay ABOVE :id or it would match as an id of "new". */}
          <Route path="/app-workflow/new" element={<Navigate to="/app-workflow" replace />} />
          <Route
            path="/app-workflow/:id"
            element={<RequirePermission module="workflows" action="view"><WorkflowDetail /></RequirePermission>}
          />
          {/* Same shape as workflows: the dialog on the list page creates the row before
              opening the editor, so the editor never handles an unsaved environment.
              /new must stay ABOVE :id or it would match as an id of "new". */}
          <Route path="/app-mock-environment/new" element={<Navigate to="/app-mock-environment" replace />} />
          <Route
            path="/app-mock-environment/:id"
            element={<RequirePermission module="mockapi" action="view"><MockApiDetail /></RequirePermission>}
          />
          <Route path="/app-ui-audit/:id" element={<RequirePermission module="uiaudits" action="view"><UiAuditDetail /></RequirePermission>} />
          <Route
            path="/app-company/:id"
            element={<RequirePermission module="companies" action="view"><CompanyDetail /></RequirePermission>}
          />
          <Route
            path="/app-connection-category/:id"
            element={<RequirePermission module="connections" action="view"><ConnectionCategoryDetail /></RequirePermission>}
          />
          <Route
            path="/app-connection-provider/:id"
            element={<RequirePermission module="connections" action="view"><ConnectionProviderDetail /></RequirePermission>}
          />
          <Route
            path="/app-agent-feedback/:id"
            element={<RequirePermission module="agents" action="view"><AgentFeedbackDetail /></RequirePermission>}
          />
          <Route
            path="/app-webhook-endpoint/:id"
            element={<RequirePermission module="calls" action="view"><WebhookEndpointDetail /></RequirePermission>}
          />
          <Route
            path="/api-tokens/:id"
            element={<RequirePermission module="core" action="view"><ApiTokenDetail /></RequirePermission>}
          />
          <Route
            path="/app-application/:id"
            element={<RequirePermission module="applications" action="view"><ApplicationDetail /></RequirePermission>}
          />
        </Route>
      </Route>

      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
    </RbacProvider>
  );
}
